// src/utils/proposalClientPdf.js
// PDF remis au client pour une proposition d'acquisition, selon le mode de prix du partage :
//   none  → aucun montant, nulle part
//   incl  → prix TTC validé par le conseiller
//   excl  → prix HT validé par le conseiller
// Un montant n'est jamais déduit de l'autre (pas de calcul HT ↔ TTC) et un prix proposé n'est jamais un prix payé.
import { makeFT } from "../locales/foundationCopy";
import { priceForMode, missingPrices } from "./offerImport";

const LOCALES = { fr: "fr-FR", en: "en-GB", de: "de-DE" };

// Ligne de prix d'une œuvre, ou null si le mode n'en affiche pas (ou si le montant du mode manque).
export function priceLine(item, mode, T, locale) {
  const amount = priceForMode(item, mode);
  if (amount == null) return null;
  const cur = item?.currency || "EUR";
  const tax = mode === "incl" ? T("TTC") : T("HT");
  return `${Number(amount).toLocaleString(locale)} ${cur} ${tax}`;
}

const slug = (s) => String(s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-zA-Z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40);

export async function imageToDataUrl(url) {
  if (!url) return null;
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const r = new FileReader();
      r.onload = () => resolve(typeof r.result === "string" ? r.result : null);
      r.onerror = () => resolve(null);
      r.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

function imageSize(dataUrl) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
    img.onerror = () => resolve(null);
    img.src = dataUrl;
  });
}

/**
 * @param {object} p
 * @param {string} p.title            nom de la proposition
 * @param {string} [p.clientLabel]
 * @param {string} [p.advisorName]
 * @param {"none"|"incl"|"excl"} p.mode
 * @param {string} p.lang             fr | en | de
 * @param {Array}  p.items            { title, artist, year, medium, dimensions, price_excl, price_incl, currency, rationale, image (dataURL|null) }
 */
export async function buildProposalClientPdf({ title, clientLabel, advisorName, mode, lang, items }) {
  const l = LOCALES[lang] ? lang : "fr";
  const locale = LOCALES[l];
  const T = makeFT(l);
  const absent = missingPrices(items, mode);
  if (absent.length) {
    const err = new Error("missing_prices");
    err.missing = absent;
    throw err;
  }

  const { default: jsPDF } = await import("jspdf");
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const W = 210, M = 18, CW = W - M * 2;
  const grey = [107, 107, 107], black = [26, 26, 26], gold = [201, 169, 97];
  const sf = (size, style = "normal", color = black) => { doc.setFontSize(size); doc.setFont("helvetica", style); doc.setTextColor(...color); };

  // Couverture
  doc.setFillColor(...black);
  doc.rect(0, 0, W, 297, "F");
  doc.setFillColor(...gold);
  doc.rect(M, 78, 2, 52, "F");
  sf(11, "normal", gold);
  doc.text(T("PROPOSITION D'ACQUISITION"), M + 8, 86);
  sf(26, "bold", [255, 255, 255]);
  doc.splitTextToSize(title || "", CW - 8).slice(0, 3).forEach((ln, i) => doc.text(ln, M + 8, 102 + i * 11));
  sf(12, "normal", [200, 200, 200]);
  if (clientLabel) doc.text(T("Pour {name}", { name: clientLabel }), M + 8, 132);
  sf(9, "normal", [140, 140, 140]);
  if (advisorName) doc.text(T("Sélectionnée par {name}", { name: advisorName }), M, 220);
  doc.text(new Date().toLocaleDateString(locale, { day: "numeric", month: "long", year: "numeric" }), M, 228);
  doc.text(T("Œuvres proposées, non détenues : le prix indiqué n'est pas un prix payé."), M, 260);

  // Une page par œuvre
  for (const it of items) {
    doc.addPage();
    doc.setFillColor(249, 247, 244);
    doc.rect(0, 0, W, 297, "F");
    let y = M;
    if (it.image) {
      const size = await imageSize(it.image);
      if (size) {
        const maxW = CW, maxH = 130;
        const r = Math.min(maxW / size.w, maxH / size.h);
        const w = size.w * r, h = size.h * r;
        try {
          doc.addImage(it.image, it.image.startsWith("data:image/png") ? "PNG" : "JPEG", M + (CW - w) / 2, y, w, h, "", "FAST");
        } catch { /* image illisible : on continue sans */ }
        y += h + 10;
      } else y += 4;
    } else {
      doc.setFillColor(238, 234, 226);
      doc.rect(M, y, CW, 60, "F");
      sf(10, "normal", grey);
      doc.text(T("Pas d'image"), M + CW / 2, y + 32, { align: "center" });
      y += 70;
    }
    sf(18, "bold");
    doc.splitTextToSize(it.title || T("Sans titre"), CW).slice(0, 2).forEach((ln) => { doc.text(ln, M, y); y += 8; });
    sf(12, "normal", [74, 69, 61]);
    doc.text([it.artist, it.year].filter(Boolean).join(", "), M, y); y += 7;
    sf(9, "normal", grey);
    const meta = [it.medium, it.dimensions].filter(Boolean).join(" · ");
    if (meta) { doc.text(meta, M, y); y += 7; }
    const price = priceLine(it, mode, T, locale);
    if (price) {
      sf(15, "bold");
      doc.text(price, M, y + 3);
      sf(8, "normal", grey);
      doc.text(T("Prix demandé"), M, y + 8);
      y += 14;
    }
    if (it.rationale) {
      y += 2;
      sf(8, "bold", [138, 131, 120]);
      doc.text(T("POURQUOI CETTE ŒUVRE"), M, y); y += 5;
      sf(10, "normal", [42, 38, 31]);
      doc.splitTextToSize(it.rationale, CW).slice(0, 12).forEach((ln) => { if (y < 280) { doc.text(ln, M, y); y += 5; } });
    }
  }

  const n = doc.getNumberOfPages();
  for (let p = 2; p <= n; p++) {
    doc.setPage(p);
    sf(8, "normal", grey);
    doc.text(`${p - 1} / ${n - 1}`, W - M, 291, { align: "right" });
    doc.text(title || "", M, 291);
  }
  return { doc, filename: `${T("proposition-")}${slug(title) || "selection"}-${mode}.pdf` };
}

export async function downloadProposalClientPdf(args) {
  const { doc, filename } = await buildProposalClientPdf(args);
  doc.save(filename);
}
